import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { ExternalLink, Settings } from "lucide-react"
import Form from "react-bootstrap/Form"
import Spinner from "react-bootstrap/Spinner"
import { useAdminToolbarPopoutPosition } from "../hooks/useAdminToolbarPopoutPosition.js"
import { ADMIN_TOOLBAR_MENU_MAX_HEIGHT } from "../helpers/adminToolbarMenuConstants.js"
import AdminPrintDialogLocationsMenu from "./AdminPrintDialogLocationsMenu.jsx"
import AdminSaveStatusIndicator from "./AdminSaveStatusIndicator.jsx"

const LOCATION_MENU_MAX_HEIGHT = ADMIN_TOOLBAR_MENU_MAX_HEIGHT
const MIN_USE_PRINT_DIALOG_MS = 500
const MIN_RELOAD_METADATA_MS = 500
const SAVE_STATUS_VISIBLE_MS = 1000
const SAVE_STATUS_FADE_MS = 500

function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function scheduleSaveStatusClear(setRowStatus, locationId, finalStatus) {
    const fadeStatus = `${finalStatus}-fade`

    window.setTimeout(() => {
        setRowStatus((prev) =>
            prev[locationId] === finalStatus ? { ...prev, [locationId]: fadeStatus } : prev
        )
    }, SAVE_STATUS_VISIBLE_MS)

    window.setTimeout(() => {
        setRowStatus((prev) => {
            if (prev[locationId] !== fadeStatus) {
                return prev
            }
            const next = { ...prev }
            delete next[locationId]
            return next
        })
    }, SAVE_STATUS_VISIBLE_MS + SAVE_STATUS_FADE_MS)
}

function scheduleReloadStatusClear(setReloadStatus, finalStatus) {
    const fadeStatus = `${finalStatus}-fade`

    window.setTimeout(() => {
        setReloadStatus((prev) => (prev === finalStatus ? fadeStatus : prev))
    }, SAVE_STATUS_VISIBLE_MS)

    window.setTimeout(() => {
        setReloadStatus((prev) => (prev === fadeStatus ? "idle" : prev))
    }, SAVE_STATUS_VISIBLE_MS + SAVE_STATUS_FADE_MS)
}

/**
 * @param {{
 *   usePrintDialog: boolean,
 *   onUsePrintDialogChange: (value: boolean) => Promise<void>,
 *   onUsePrintDialogChangeForLocation?: (locationId: number, value: boolean) => Promise<void>,
 *   onReloadMetadata?: () => Promise<{ status: number, body?: unknown }>,
 *   locations?: Array<{ id?: number | string, name?: string, lodge_code?: string, use_print_dialog?: boolean }>,
 *   currentLocationId?: number | null,
 *   disabled?: boolean,
 *   isOpen?: boolean,
 *   onOpenChange?: (open: boolean) => void,
 *   lodgeCode?: string,
 *   toolbarBg?: string,
 * }} props
 */
export default function AdminQuickSettings({
    usePrintDialog,
    onUsePrintDialogChange,
    onUsePrintDialogChangeForLocation,
    onReloadMetadata,
    locations = [],
    currentLocationId = null,
    disabled = false,
    isOpen = false,
    onOpenChange,
    lodgeCode = "—",
    toolbarBg = "#0f6170",
}) {
    const [view, setView] = useState("main")
    const [mainPending, setMainPending] = useState(false)
    const [rowStatus, setRowStatus] = useState(/** @type {Record<string, string>} */({}))
    const [reloadStatus, setReloadStatus] = useState(
        /** @type {"idle" | "saving" | "success" | "success-fade" | "error" | "error-fade"} */("idle")
    )

    const isSubmenu = view === "print-dialog-list"
    const reloadPending = reloadStatus === "saving"
    const { triggerRef, menuRef, menuStyle } = useAdminToolbarPopoutPosition(isOpen, {
        maxHeightCap: isSubmenu ? LOCATION_MENU_MAX_HEIGHT : null,
    })

    useEffect(() => {
        if (!isOpen) {
            setView("main")
            setRowStatus({})
            setReloadStatus("idle")
        }
    }, [isOpen])

    function setIsOpen(open) {
        onOpenChange?.(open)
    }

    async function handleMainToggle(checked) {
        if (mainPending || disabled || checked === usePrintDialog) {
            return
        }

        setMainPending(true)
        try {
            await Promise.all([onUsePrintDialogChange(checked), wait(MIN_USE_PRINT_DIALOG_MS)])
        } catch (e) {
            console.error("Failed to update use print dialog setting:", e)
        } finally {
            setMainPending(false)
        }
    }

    async function handleLocationToggle(locationId, checked) {
        if (!onUsePrintDialogChangeForLocation) {
            setRowStatus((prev) => {
                const next = { ...prev }
                delete next[locationId]
                return next
            })
            return
        }

        try {
            await Promise.all([
                onUsePrintDialogChangeForLocation(locationId, checked),
                wait(MIN_USE_PRINT_DIALOG_MS),
            ])
            setRowStatus((prev) => ({ ...prev, [locationId]: "success" }))
            scheduleSaveStatusClear(setRowStatus, locationId, "success")
        } catch (e) {
            console.error("Failed to update use print dialog for location:", e)
            setRowStatus((prev) => ({ ...prev, [locationId]: "error" }))
            scheduleSaveStatusClear(setRowStatus, locationId, "error")
        }
    }

    async function handleReloadMetadata() {
        if (!onReloadMetadata || reloadPending || disabled) {
            return
        }

        setReloadStatus("saving")
        try {
            const [result] = await Promise.all([onReloadMetadata(), wait(MIN_RELOAD_METADATA_MS)])
            if (!result || result.status < 200 || result.status >= 300) {
                throw new Error(`Reload metadata failed with status ${result?.status ?? 0}`)
            }
            setReloadStatus("success")
            scheduleReloadStatusClear(setReloadStatus, "success")
        } catch (e) {
            console.error("Failed to reload metadata:", e)
            setReloadStatus("error")
            scheduleReloadStatusClear(setReloadStatus, "error")
        }
    }

    function openPrintDialogSubmenu(e) {
        e.stopPropagation()
        setView("print-dialog-list")
    }

    function handleBack() {
        setView("main")
    }

    const menuClassName =
        "lodge-admin-toolbar-popout-menu " +
        (isSubmenu ? "lodge-admin-print-dialog-submenu-menu" : "lodge-admin-quick-settings-menu")

    return (
        <div className="lodge-admin-toolbar-cell lodge-admin-toolbar-cell--control lodge-admin-quick-settings">
            <button
                type="button"
                ref={triggerRef}
                className={
                    "lodge-admin-toolbar-cell-btn lodge-admin-quick-settings-trigger" +
                    (mainPending ? " lodge-admin-quick-settings-trigger--loading" : "")
                }
                onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen(!isOpen)
                }}
                disabled={disabled || mainPending}
                aria-expanded={isOpen}
                aria-busy={mainPending}
                aria-haspopup="menu"
                aria-label={mainPending ? "Updating settings…" : "Quick settings"}
            >
                {mainPending ? (
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Updating settings…</span>
                    </Spinner>
                ) : (
                    <Settings size={24} aria-hidden="true" />
                )}
            </button>

            {isOpen
                ? createPortal(
                    <div
                        ref={menuRef}
                        className={menuClassName}
                        style={{
                            ...menuStyle,
                            background: toolbarBg,
                            "--lodge-admin-toolbar-bg": toolbarBg,
                        }}
                        role="menu"
                        aria-label={isSubmenu ? "Use print dialog by location" : "Quick settings"}
                    >
                        {isSubmenu ? (
                            <AdminPrintDialogLocationsMenu
                                locations={locations}
                                currentLocationId={currentLocationId}
                                rowStatus={rowStatus}
                                setRowStatus={setRowStatus}
                                onBack={handleBack}
                                onToggle={handleLocationToggle}
                                disabled={disabled}
                            />
                        ) : (
                            <>
                                <div className="lodge-admin-quick-settings-row">
                                    <Form.Check
                                        type="switch"
                                        id="lodge-admin-use-print-dialog"
                                        className="lodge-admin-quick-settings-switch"
                                        label={`${lodgeCode} - Use Print Dialog`}
                                        checked={usePrintDialog}
                                        disabled={mainPending || disabled}
                                        onChange={(e) => handleMainToggle(e.target.checked)}
                                    />
                                    <button
                                        type="button"
                                        className="lodge-admin-quick-settings-open-btn"
                                        disabled={disabled}
                                        aria-label="Manage use print dialog for all locations"
                                        title="Manage all locations"
                                        onClick={openPrintDialogSubmenu}
                                    >
                                        <ExternalLink size={14} aria-hidden="true" />
                                    </button>
                                </div>
                                {onReloadMetadata ? (
                                    <button
                                        type="button"
                                        className="lodge-admin-quick-settings-action"
                                        role="menuitem"
                                        disabled={disabled || reloadPending}
                                        aria-busy={reloadPending}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleReloadMetadata()
                                        }}
                                    >
                                        <span className="lodge-admin-quick-settings-action-label">
                                            Server - Reload Metadata
                                        </span>
                                        <AdminSaveStatusIndicator status={reloadStatus} />
                                    </button>
                                ) : null}
                            </>
                        )}
                    </div>,
                    document.body
                )
                : null}
        </div>
    )
}
